/**
 * Organisation settings: name, partner type, media retention, plan (a stub
 * until billing ships), and the Partner Terms the organisation accepted
 * (`GET /partner/terms`), and Close organisation (owners ask FinderlyFix staff;
 * CloseOrganisation.tsx). The DPA is available on request, not linked. Links out to the other Settings areas (team, keys,
 * webhooks, audit), which are their own screens.
 */
import { useEffect, useRef, useState } from 'react';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { PARTNER_TYPES, type PartnerType } from '../api/types';
import { Link, useUnsavedChangesGuard } from '../router';
import { CopyButton, ErrorNotice, Field, PageHead, formatDateTime, useToast } from '../ui/kit';
import { PendingExplanation, PlanExplanation, planLabel, statusLabel } from '../org/status';
import { useMutation, useQuery } from '../ui/hooks';
import { LEGAL_EMAIL, PARTNER_TERMS_ANCHOR, TERMS_PATH, legalVersionLabel } from '../legal/current';
import { useTermsReview } from '../legal/TermsGate';
import { PARTNER_TYPE_COPY } from './OrgSetup';
import { NetworkFallbackRow } from './NetworkFallback';
import { CloseOrganisation } from './CloseOrganisation';
import { NoticeRecipients } from '../org/NoticeRecipients';
import { fallbackNetworkFor } from '../config/model';

export function Settings() {
  const { api, org, role, refreshOrg, termsRequired } = useSession();
  const review = useTermsReview();
  // Re-read when the modal accepts, so the accepted version shows at once.
  const terms = useQuery(() => api.getTerms(), ['terms', org?.id, termsRequired, review.acceptedCount]);
  const termsRef = useRef<HTMLElement>(null);
  // Arriving from an "accept the Partner Terms" notice: bring the panel into view.
  useEffect(() => {
    if (window.location.hash === `#${PARTNER_TERMS_ANCHOR}`) {
      termsRef.current?.scrollIntoView?.({ block: 'center' });
      termsRef.current?.focus({ preventScroll: true });
    }
  }, []);
  const termsOwed = Boolean(terms.data && (terms.data.terms_required || !terms.data.accepted));
  const toast = useToast();
  const [name, setName] = useState(org?.display_name ?? '');
  const [type, setType] = useState<PartnerType>((org?.partner_type as PartnerType) ?? 'other');
  const [retention, setRetention] = useState(String(org?.media_retention_days ?? 30));
  useEffect(() => {
    setName(org?.display_name ?? '');
    setType((org?.partner_type as PartnerType) ?? 'other');
    setRetention(String(org?.media_retention_days ?? 30));
  }, [org]);
  const editable = can(role, 'editOrg');
  const save = useMutation(() => api.updateOrg({ display_name: name.trim(), partner_type: type, media_retention_days: Number(retention) }));
  // Migration 082: a move into a neutral type turns the Network fallback off
  // wherever it was on. Said before saving, and after, naming the sites.
  const [fallbackOff, setFallbackOff] = useState<string[] | null>(null);
  const intoNeutral = fallbackNetworkFor(type) === 'neutral' && fallbackNetworkFor(org?.partner_type) !== 'neutral';
  const days = Number(retention);
  const retentionIssue = Number.isInteger(days) && days >= 1 && days <= 3650 ? [] : [{ path: 'media_retention_days', message: 'Between 1 and 3650 days.' }];
  const nameIssue = name.trim() ? [] : [{ path: 'display_name', message: 'Enter your organisation’s name. Homeowners see it in the widget.' }];
  const dirty =
    editable &&
    Boolean(org) &&
    (name !== (org?.display_name ?? '') || type !== org?.partner_type || retention !== String(org?.media_retention_days ?? 30));
  useUnsavedChangesGuard(dirty);

  return (
    <div>
      <PageHead title="Organisation" />
      <div className="grid-2" style={{ alignItems: 'start' }}>
        <form
          className="panel panel-pad stack"
          onSubmit={async (e) => {
            e.preventDefault();
            const saved = await save.run();
            if (saved) {
              setFallbackOff(saved.network_fallback_turned_off?.map((x) => x.label) ?? null);
              await refreshOrg();
              toast('Organisation updated');
            }
          }}
        >
          <Field label="Name" hint="Homeowners see it in the widget (“Send to …”, the consent line)." issues={nameIssue}>
            {(p) => <input {...p} className="input" value={name} disabled={!editable} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field
            label="Partner type"
            hint={
              intoNeutral
                ? 'Saving turns the Network fallback off on every site where it is on: warranty, insurer and membership organisations need a neutral network, which isn’t available yet.'
                : 'Picks which contractor network the Network fallback uses, and whether you can turn it on yourself. Not shown to homeowners.'
            }
          >
            {(p) => (
              <select {...p} className="input" value={type} disabled={!editable} onChange={(e) => setType(e.target.value as PartnerType)}>
                {PARTNER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {PARTNER_TYPE_COPY[t].label}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Keep photos and recordings for (days)" hint={
              <>
                After this, lead media is deleted. Contact details follow the retention rules in{' '}
                <a href={`${TERMS_PATH}#data`} target="_blank" rel="noreferrer">
                  Partner Terms section 6
                </a>
                .
              </>
            }
            issues={retentionIssue}>
            {(p) => <input {...p} className="input num" type="number" min={1} max={3650} value={retention} disabled={!editable} onChange={(e) => setRetention(e.target.value)} />}
          </Field>
          <ErrorNotice error={save.error} />
          {fallbackOff?.length ? (
            <div className="notice warn small" role="status">
              Network fallback turned off for {fallbackOff.join(', ')}: a neutral network isn’t available for this partner type yet.
            </div>
          ) : null}
          {editable ? (
            <div>
              <button className="btn btn-primary" type="submit" disabled={save.pending || retentionIssue.length > 0 || nameIssue.length > 0 || !dirty}>
                {save.pending ? 'Saving…' : 'Save'}
              </button>
            </div>
          ) : (
            <p className="small muted">Only owners and admins can change these.</p>
          )}
        </form>
        <div className="stack">
          <section className="panel panel-pad stack-sm" aria-labelledby="plan-h">
            <div className="row-between">
              <h2 id="plan-h" className="h-sub">Plan</h2>
              <span className="tag accent">{planLabel(org?.plan)}</span>
            </div>
            <div className="small muted">
              <PlanExplanation plan={org?.plan} />
            </div>
            <hr className="divider" style={{ margin: '4px 0' }} />
            <div className="row-between">
              <h3 className="h-sub" style={{ fontSize: 14 }}>Status</h3>
              <span className={`tag ${org?.status === 'pending' ? 'warn' : org?.status === 'active' ? 'ok' : org?.status === 'suspended' ? 'bad' : ''}`}>{statusLabel(org?.status)}</span>
            </div>
            {org?.status === 'pending' ? (
              <div className="small muted">
                <PendingExplanation />
              </div>
            ) : null}
            <div className="row small muted org-id" style={{ gap: 6, flexWrap: 'wrap' }}>
              <span>Org ID</span>
              <code className="key">{org?.id}</code>
              <CopyButton text={org?.id ?? ''} label="Copy" className="btn btn-sm btn-ghost" />
            </div>
          </section>
          <NetworkFallbackRow />
          <NoticeRecipients />
          <section id={PARTNER_TERMS_ANCHOR} ref={termsRef} tabIndex={-1} className="panel panel-pad stack-sm" aria-labelledby="terms-h">
            <h2 id="terms-h" className="h-sub">Partner Terms</h2>
            {terms.data?.accepted ? (
              <p className="small muted">
                Accepted the version of {legalVersionLabel(terms.data.accepted.terms_version)}
                {terms.data.accepted.dpa_version ? (
                  <>
                    {' '}
                    (with the DPA of {legalVersionLabel(terms.data.accepted.dpa_version)})
                  </>
                ) : null}{' '}
                on {formatDateTime(terms.data.accepted.accepted_at)}.
              </p>
            ) : terms.data ? (
              <p className="small muted">Not accepted yet.</p>
            ) : null}
            {terms.data?.terms_required && terms.data.accepted ? (
              <p className="small muted">
                A newer version ({legalVersionLabel(terms.data.current.terms_version)}) is waiting for an owner or admin to accept.
              </p>
            ) : null}
            <ErrorNotice error={terms.error} />
            {termsOwed ? (
              can(role, 'acceptTerms') ? (
                <div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={review.open}>
                    Review and accept
                  </button>
                </div>
              ) : (
                <p className="small">Ask an owner or admin to review and accept them here.</p>
              )
            ) : null}
            <p className="small">
              <a href={TERMS_PATH} target="_blank" rel="noreferrer">
                Read the Partner Terms
              </a>
            </p>
            <p className="small muted">
              A data processing addendum is available on request from <a href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a>. Once signed, it controls over the Terms on data protection.
            </p>
          </section>
          <section className="panel panel-pad">
            <ul className="stack-sm" style={{ margin: 0, paddingLeft: 18 }}>
              {can(role, 'members') ? <li><Link to="/team">Team & roles</Link></li> : null}
              {can(role, 'sites') ? <li><Link to="/sites">Site keys and rotation</Link></li> : null}
              {can(role, 'destinations') ? <li><Link to="/destinations">Webhooks and destinations</Link></li> : null}
              {can(role, 'audit') ? <li><Link to="/audit">Audit log</Link></li> : null}
            </ul>
          </section>
          <CloseOrganisation />
        </div>
      </div>
    </div>
  );
}
